import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioButton,
} from '../../components/BrioComponents';
import { useBoqRates, useVendorLookup } from '../hooks/useBoqRates';
import { useBoqCostIndex } from '../hooks/useBoqCostIndex';
import { RateAgePill } from './RateAgePill';
import { formatMoney } from '../utils/money';

// Per-line rate library: observation history for the line's item_version, an
// add-observation form (with vendor / city / date / consultant / filename),
// and Apply quoted/billed to set the line's rate. Rates carry a rate_date, so
// each is also shown escalated to today via the construction cost index — the
// "Adjusted" value is what the Apply buttons autofill (the QS can still type a
// custom rate over it in the grid). See utils/escalation.js for the rationale.
function RateLibraryModal({ line, currency = 'INR', billView = 'quoted', onClose, onApplyRate }) {
  const money = (v) => (v === null || v === undefined ? '—' : formatMoney(v, currency));
  // The basis the estimator is currently editing — its Apply button is emphasised.
  const currentBasis = billView === 'billed' ? 'billed' : 'quoted';
  const itemVersionId = line.version?.id;
  const { rates, loading, error, refresh, createRate, deleteRate } = useBoqRates(itemVersionId);
  const costIndex = useBoqCostIndex();
  const vendors = useVendorLookup();

  // Pricing date for the new BOQ: today. The cost index escalates each
  // observation's rate_date to here. roundRate keeps autofilled values tidy.
  const targetDate = new Date().toISOString().slice(0, 10);
  const targetLabel = new Date(targetDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  const roundRate = (v) => (v == null ? null : Math.round(v * 100) / 100);
  const adjust = (value, rateDate) => roundRate(costIndex.adjust(value, rateDate, currency, targetDate));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    rateQuoted: '', rateBilled: '', vendorId: '', city: '',
    rateDate: new Date().toISOString().slice(0, 10),
    contractor: '', sourceConsultant: '', sourceFilename: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  const vendorName = useMemo(() => {
    const m = new Map();
    vendors.forEach((v) => m.set(v.id, v.name));
    return (id) => (id ? (m.get(id) || `#${id}`) : '');
  }, [vendors]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleAdd = async () => {
    setFormError(null);
    setSubmitting(true);
    const result = await createRate(form);
    setSubmitting(false);
    if (result.success) {
      setShowForm(false);
      setForm({ rateQuoted: '', rateBilled: '', vendorId: '', city: '', rateDate: new Date().toISOString().slice(0, 10), contractor: '', sourceConsultant: '', sourceFilename: '' });
    } else {
      setFormError(result.error || 'Failed to record rate.');
    }
  };

  const footer = <BrioButton variant="secondary" onClick={onClose}>Close</BrioButton>;

  return (
    <BrioModal
      title={`Rates — ${line.version?.fullCode || ''} ${line.version?.shortDescription || ''}`}
      onClose={onClose}
      width="fullwidth"
      footer={footer}
    >
      <BrioModalBody>
        {error && <div style={{ color: '#a94442', marginBottom: '0.75rem' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
            {loading ? 'Loading…' : `${rates.length} observation${rates.length === 1 ? '' : 's'} for this item`}
            {!loading && !costIndex.loading && (
              costIndex.hasData
                ? <span style={{ marginLeft: 8 }}>· rates escalated to <strong>{targetLabel}</strong> via construction index</span>
                : <span style={{ marginLeft: 8, color: '#a9772a' }}>· no cost index set — showing original rates</span>
            )}
            {!loading && rates.length > 0 && (
              <span style={{ marginLeft: 8 }}>· applying to the <strong>{currentBasis === 'billed' ? 'tender' : 'estimate'}</strong> basis (highlighted); <em>*</em> = filled from the other basis</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <BrioButton variant="secondary" onClick={refresh}>Refresh</BrioButton>
            <BrioButton variant="primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Cancel' : '+ Record rate'}</BrioButton>
          </div>
        </div>

        {showForm && (
          <div style={{ border: '1px solid var(--brio-border, #ccc)', borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', background: 'var(--brio-surface-muted, #f7f7f5)' }}>
            {formError && <div style={{ color: '#a94442', marginBottom: '0.5rem' }}>{formError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.6rem' }}>
              <BrioModalField label="Quoted rate"><BrioInput type="number" step="any" value={form.rateQuoted} onChange={update('rateQuoted')} /></BrioModalField>
              <BrioModalField label="Billed rate"><BrioInput type="number" step="any" value={form.rateBilled} onChange={update('rateBilled')} /></BrioModalField>
              <BrioModalField label="Vendor">
                <BrioSelect value={form.vendorId} onChange={update('vendorId')}>
                  <option value="">—</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </BrioSelect>
              </BrioModalField>
              <BrioModalField label="Contractor (free text)"><BrioInput value={form.contractor} onChange={update('contractor')} placeholder="if not a listed vendor" /></BrioModalField>
              <BrioModalField label="City"><BrioInput value={form.city} onChange={update('city')} placeholder="e.g. Mumbai" /></BrioModalField>
              <BrioModalField label="Date"><BrioInput type="date" value={form.rateDate} onChange={update('rateDate')} /></BrioModalField>
              <BrioModalField label="Consultant" hint="Source consultant/firm, e.g. for a shared sample BOQ"><BrioInput value={form.sourceConsultant} onChange={update('sourceConsultant')} /></BrioModalField>
              <BrioModalField label="Source filename" hint="If imported from a BOQ file"><BrioInput value={form.sourceFilename} onChange={update('sourceFilename')} /></BrioModalField>
            </div>
            <div style={{ marginTop: '0.6rem', textAlign: 'right' }}>
              <BrioButton variant="primary" onClick={handleAdd} disabled={submitting}>{submitting ? 'Saving…' : 'Save observation'}</BrioButton>
            </div>
          </div>
        )}

        <div style={{ overflowX: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead style={{ background: 'var(--brio-surface-muted, #f7f7f5)' }}>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '0.4rem 0.5rem' }}>Date</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Quoted</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Billed</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }} title={`Escalated to ${targetLabel} using the construction cost index`}>Adjusted<br /><span style={{ fontWeight: 400, fontSize: '0.68rem', color: 'var(--brio-text-muted)' }}>→ {targetLabel}</span></th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Vendor / contractor</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>City</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Consultant</th>
                <th style={{ padding: '0.4rem 0.5rem' }}>Source file</th>
                <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Apply</th>
                <th style={{ padding: '0.4rem 0.5rem' }} />
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: '0.75rem', color: 'var(--brio-text-muted)' }}>No rate observations yet. Record one above, then apply it to the line.</td></tr>
              ) : rates.map((r) => {
                const adjQuoted = adjust(r.rateQuoted, r.rateDate);
                const adjBilled = adjust(r.rateBilled, r.rateDate);
                const factor = costIndex.factor(r.rateDate, currency, targetDate);
                return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)' }}>
                  <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{r.rateDate || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{money(r.rateQuoted)}</td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{money(r.rateBilled)}</td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}
                      title={factor != null ? `×${factor.toFixed(3)} from ${r.rateDate}` : 'No cost index for this date/region'}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                      {adjQuoted != null ? <span style={{ fontWeight: 500 }}>{money(adjQuoted)}</span> : <span style={{ color: 'var(--brio-text-muted)' }}>{money(r.rateQuoted)}</span>}
                      <RateAgePill date={r.rateDate} />
                    </span>
                    {adjBilled != null && <div style={{ fontSize: '0.72rem', color: 'var(--brio-text-muted)' }}>{money(adjBilled)}</div>}
                    {factor != null && <div style={{ fontSize: '0.66rem', color: '#9F902A' }}>×{factor.toFixed(2)}</div>}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>{vendorName(r.vendorId) || r.contractor || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>{r.city || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem' }}>{r.sourceConsultant || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem', fontSize: '0.76rem', color: 'var(--brio-text-muted)' }}>{r.sourceFilename || '—'}</td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(r.rateQuoted == null && r.rateBilled == null) ? (
                      <span style={{ color: 'var(--brio-text-muted)' }}>—</span>
                    ) : (
                      // Offer Apply for BOTH bases for any observation. Each basis
                      // prefers its own rate but falls back to the other (cross),
                      // so a tender-only observation can still seed an estimate —
                      // and vice versa. The basis being edited is emphasised.
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {['quoted', 'billed'].map((basis) => {
                          const adjSelf = basis === 'quoted' ? adjQuoted : adjBilled;
                          const adjOther = basis === 'quoted' ? adjBilled : adjQuoted;
                          const origSelf = basis === 'quoted' ? r.rateQuoted : r.rateBilled;
                          const origOther = basis === 'quoted' ? r.rateBilled : r.rateQuoted;
                          const adjVal = adjSelf != null ? adjSelf : adjOther;
                          const origVal = origSelf != null ? origSelf : origOther;
                          const applyVal = adjVal != null ? adjVal : origVal;
                          if (applyVal == null) return null;
                          const cross = origSelf == null; // using the other basis' rate
                          const label = basis === 'quoted' ? 'Estimate' : 'Tender';
                          const isCurrent = basis === currentBasis;
                          return (
                            <span key={basis} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                              <button type="button" onClick={() => onApplyRate(applyVal, basis)}
                                style={isCurrent ? applyBtnPrimary : applyBtn}
                                title={`${cross ? `From the ${basis === 'quoted' ? 'tender' : 'estimate'} rate. ` : ''}Set ${label.toLowerCase()} rate to ${money(applyVal)}`}>
                                → {label}{cross ? '*' : ''}
                              </button>
                              {adjVal != null && origVal != null && adjVal !== origVal && (
                                <button type="button" onClick={() => onApplyRate(origVal, basis)} style={origLink} title={`Use original ${money(origVal)} instead`}>orig</button>
                              )}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                    <button type="button" onClick={() => deleteRate(r.id)} title="Delete observation"
                      style={{ border: 'none', background: 'transparent', color: '#a94442', cursor: 'pointer' }}>×</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </BrioModalBody>
    </BrioModal>
  );
}

const applyBtn = {
  fontSize: '0.74rem', padding: '0.15rem 0.5rem',
  border: '1px solid var(--brio-gold, #9F902A)', color: 'var(--brio-gold, #9F902A)',
  background: 'transparent', borderRadius: 4, cursor: 'pointer',
};

// The basis currently being edited — filled gold so the right target stands out.
const applyBtnPrimary = {
  ...applyBtn,
  color: '#fff', background: 'var(--brio-gold, #9F902A)', fontWeight: 600,
};

// Subtle secondary action to apply the un-escalated original rate instead.
const origLink = {
  fontSize: '0.66rem', padding: '0.1rem 0.25rem', border: 'none',
  background: 'transparent', color: 'var(--brio-text-muted, #888)',
  textDecoration: 'underline', cursor: 'pointer',
};

export default RateLibraryModal;
